import DOMPurify from "dompurify";
import { FiBarChart2 } from "react-icons/fi";
import { cn } from "utils/helpers";
import SkeletonBase from "newui/components/Skeleton";
import { useComparisonData } from "../../../../../hooks/usePanelScoringQueries";

interface ComparisonTableProps {
	rfxId: number;
	evalId: number;
	criteriaId: number | undefined;
	inviteId: number | undefined;
}

const formatDate = (dateStr: string | null | undefined): string => {
	if (!dateStr) {
		return "—";
	}
	const date = new Date(dateStr);
	return date.toLocaleDateString("en-AU", {
		day: "2-digit",
		month: "short",
		year: "numeric",
	});
};

const sanitizeHtml = (html: string | null): string => {
	if (!html) {
		return "";
	}
	return DOMPurify.sanitize(html, {
		ALLOWED_TAGS: ["b", "i", "em", "strong", "p", "br", "ul", "ol", "li", "span", "u", "h1", "h2", "h3"],
		ALLOWED_ATTR: ["style"],
		FORBID_TAGS: ["a", "script", "style", "iframe"],
	});
};

export const ComparisonTable: React.FC<ComparisonTableProps> = ({
	rfxId,
	evalId,
	criteriaId,
	inviteId,
}) => {
	const { data, isLoading, error } = useComparisonData(
		rfxId,
		evalId,
		criteriaId,
		inviteId
	);

	if (!criteriaId || !inviteId) {
		return (
			<div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-gray-400 text-sm">
				Select a criterion and supplier to view comparison data.
			</div>
		);
	}

	if (isLoading) {
		return (
			<div className="space-y-2" data-testid="comparison-table-loading">
				{[1, 2, 3].map((i) => (
					<SkeletonBase key={i} startColor="lightgray" width="100%" height="48px" className="!rounded-lg" />
				))}
			</div>
		);
	}

	if (error || !data) {
		return (
			<p className="text-sm text-red-600" data-testid="comparison-table-error">
				Failed to load comparison data.
			</p>
		);
	}

	const { members, stats } = data;

	return (
		<div data-testid="comparison-table">
			{/* Stats bar */}
			{stats && (
				<div className="mb-4 flex flex-wrap items-center gap-4 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5">
					<span className="text-xs font-medium uppercase tracking-wider text-gray-400">
						Stats
					</span>
					<div className="flex flex-wrap items-center gap-3">
						{stats.average !== null && (
							<span className="inline-flex items-center gap-1 text-sm">
								<FiBarChart2 size={12} className="text-[#0031DD]" aria-hidden="true" />
								<span className="text-gray-500">Avg:</span>
								<span className="font-medium tabular-nums">{stats.average.toFixed(1)}</span>
							</span>
						)}
						{stats.min !== null && (
							<span className="text-sm">
								<span className="text-gray-500">Min:</span>{" "}
								<span className="font-medium tabular-nums">{stats.min}</span>
							</span>
						)}
						{stats.max !== null && (
							<span className="text-sm">
								<span className="text-gray-500">Max:</span>{" "}
								<span className="font-medium tabular-nums">{stats.max}</span>
							</span>
						)}
						{stats.range !== null && (
							<span className="text-sm">
								<span className="text-gray-500">Range:</span>{" "}
								<span className="font-medium tabular-nums">{stats.range}</span>
							</span>
						)}
						<span className="text-sm">
							<span className="text-gray-500">Responses:</span>{" "}
							<span className="font-medium tabular-nums">{stats.count}</span>
						</span>
					</div>
				</div>
			)}

			{/* Comparison table */}
			<div className="w-full overflow-x-auto">
				<table className="w-full min-w-[700px] border-collapse text-sm">
					<thead>
						<tr className="border-b border-gray-200 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
							<th className="px-4 py-3">Response From</th>
							<th className="px-4 py-3">Role</th>
							<th className="px-4 py-3 text-right w-20">Score</th>
							<th className="px-4 py-3">Comments</th>
						</tr>
					</thead>
					<tbody>
						{members.map((member) => {
							const sanitized = sanitizeHtml(member.comment);
							return (
								<tr
									key={member.member_id}
									className="border-b border-gray-100 transition-colors hover:bg-gray-50"
									data-testid={`comparison-row-${member.member_id}`}
								>
									<td className="px-4 py-3">
										<span className="font-medium text-gray-900">
											{member.member_name || "Anonymous"}
										</span>
									</td>
									<td className="px-4 py-3">
										<span
											className={cn(
												"inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize",
												member.role === "chair"
													? "bg-purple-100 text-purple-700"
													: "bg-gray-100 text-gray-700"
											)}
										>
											{member.role}
										</span>
									</td>
									<td className="px-4 py-3 text-right">
										<span
											className={cn(
												"inline-flex min-w-[2rem] justify-center rounded-md px-2 py-0.5 text-sm font-semibold tabular-nums",
												member.score !== null
													? "bg-[#e8eeff] text-[#0031DD]"
													: "text-gray-400"
											)}
										>
											{member.score !== null ? member.score : "—"}
										</span>
									</td>
									<td className="px-4 py-3 text-gray-600 text-sm max-w-[300px]">
										{sanitized ? (
											<div
												className="prose prose-sm max-w-none [&>p]:m-0"
												dangerouslySetInnerHTML={{ __html: sanitized }}
											/>
										) : (
											<span className="text-gray-400">—</span>
										)}
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			{members.length === 0 && (
				<div className="py-6 text-center text-sm text-gray-400">
					No comparison data available for this criterion.
				</div>
			)}
		</div>
	);
};
